import { message } from "antd";
import { InternalAxiosRequestConfig } from "axios";
import dayjs from "dayjs";
import { jwtDecode } from "jwt-decode";
import { MiddlewareCallbackParams } from "openapi-fetch";
import { oldBaseApi } from "@shared/api";

export const storageAccessTokenName = "newToken";
const storageIatName = "newIat";
export const storageOldAccessTokenName = "token";
const storageOldIatName = "iat";

function isTokenExpired(token: string | null | undefined): boolean {
  if (!token) return false;

  const { exp } = jwtDecode(token);

  return Boolean(exp && dayjs().isAfter(dayjs.unix(exp)));
}

export class TokenManipulate {
  static print(tokens: {
    newToken?: string;
    refreshToken?: string;
    token?: string;
    oldRefreshToken?: string;
  }) {
    if (tokens.newToken) {
      localStorage.setItem(storageAccessTokenName, tokens.newToken);
    }
    if (tokens.token) {
      localStorage.setItem(storageOldAccessTokenName, tokens.token);
    }
    if (tokens.refreshToken) {
      // refresh token handling is not implemented yet
    }
    if (tokens.oldRefreshToken) {
      // old refresh token handling is not implemented yet
    }
  }

  static async refresh(refreshToken: string) {
    const messageKey = "updateToken";
    message.loading({
      key: messageKey,
      content: "Обновление токена",
    });

    if (!refreshToken) {
      return Promise.reject();
    }

    try {
      const tokens = await oldBaseApi
        .post(`api/tokens/jwt/refresh`, refreshToken)
        .then((response) => response.data);

      this.print({
        token: tokens.refreshToken,
      });

      message.success({
        key: messageKey,
        content: "Токен обновлен",
      });
    } catch (e) {
      message.error({
        key: messageKey,
        content: "Не удалось обновить токен",
      });
    }
  }

  static remove() {
    localStorage.removeItem(storageAccessTokenName);
    localStorage.removeItem(storageIatName);
    localStorage.removeItem(storageOldAccessTokenName);
    localStorage.removeItem(storageOldIatName);
  }
}

export const checkTokenMiddleware = async ({
  request,
}: {
  request: MiddlewareCallbackParams["request"];
}) => {
  const token = localStorage.getItem(storageAccessTokenName);
  const oldToken = localStorage.getItem(storageOldAccessTokenName);

  const updateToken = async () => {
    try {
      await TokenManipulate.refresh(oldToken || "");
      const updatedToken = localStorage.getItem(storageAccessTokenName);
      const updatedOldToken = localStorage.getItem(storageOldAccessTokenName);
      request.headers.set("Authorization", `Bearer ${updatedToken}`);
      request.headers.set("x-auth", `${updatedOldToken}`);
      return request;
    } catch {
      try {
        await TokenManipulate.refresh(oldToken || "");
        const updatedToken = localStorage.getItem(storageAccessTokenName);
        const updatedOldToken = localStorage.getItem(storageOldAccessTokenName);
        request.headers.set("x-auth", `${updatedOldToken}`);
        request.headers.set("Authorization", `Bearer ${updatedToken}`);
        return request;
      } catch {
        // token refresh failed twice
      }
      return request;
    }
  };

  if (isTokenExpired(token) || isTokenExpired(oldToken)) {
    return updateToken();
  }

  // если токена нет (для первого запроса, без синхронизации после авторизации на bd 1.0
  if (!token && oldToken) {
    return updateToken();
  }

  if (request.headers.get("Authorization") === "Bearer ") {
    return updateToken();
  }

  const currentToken = localStorage.getItem(storageAccessTokenName);

  if (currentToken) {
    // eslint-disable-next-line no-param-reassign
    const updatedOldToken = localStorage.getItem(storageOldAccessTokenName);
    request.headers.set("Authorization", `Bearer ${currentToken}`);
    request.headers.set("x-auth", `${updatedOldToken}`);
    return request;
  }
};

export const checkToken = async (
  config: InternalAxiosRequestConfig,
  settings = { useOldToken: false },
) => {
  const usableTokenName = settings.useOldToken
    ? storageOldAccessTokenName
    : storageAccessTokenName;

  if (config.url?.includes(`/tokens/jwt/refresh`)) return config;

  const token = localStorage.getItem(usableTokenName);
  const oldToken = localStorage.getItem(storageOldAccessTokenName);

  const updateToken = async () => {
    try {
      await TokenManipulate.refresh(oldToken || "");
      const updatedToken = localStorage.getItem(usableTokenName);
      // eslint-disable-next-line no-param-reassign
      config.headers.Authorization = `Bearer ${updatedToken}`;
      return config;
    } catch {
      try {
        await TokenManipulate.refresh(oldToken || "");
        const updatedToken = localStorage.getItem(usableTokenName);
        // eslint-disable-next-line no-param-reassign
        config.headers.Authorization = `Bearer ${updatedToken}`;
        return config;
      } catch {
        // token refresh failed twice
      }
      return config;
    }
  };

  if (isTokenExpired(token)) {
    return updateToken();
  }

  // если токена нет (для первого запроса, без синхронизации после авторизации на bd 1.0
  if (!token && oldToken) {
    return updateToken();
  }

  if (config.headers?.Authorization?.toString() === "Bearer ") {
    return updateToken();
  }

  const currentToken = localStorage.getItem(usableTokenName);

  if (currentToken) {
    // eslint-disable-next-line no-param-reassign
    config.headers.Authorization = `Bearer ${currentToken}`;
    return config;
  }

  return config;
};

export { isSuperAdminByToken, isSuperAdminFromStorage } from "./token-roles";
